/**
 * SidebarBuilderPage — UI per personalizzare la sidebar (per utente).
 *
 * Funzionalità:
 *  - Riordina voci (drag&drop / pulsanti su/giù)
 *  - Nascondi voci che non usi
 *  - Reset alle preferenze di default
 *
 * Persistenza: users.preferences.sidebar = { order: [to1, to2, ...], hidden: [to1, to2, ...] }
 */
import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowUp, ArrowDown, RotateCcw, Save, Loader2 } from "lucide-react";

// Lista voci default — DEVE essere sincronizzata con Sidebar.jsx
const ALL_ITEMS = [
  { to: "/", label: "Dashboard" },
  { to: "/clienti", label: "Clienti" },
  { to: "/agenda", label: "Agenda" },
  { to: "/calendar", label: "Calendario" },
  { to: "/pipeline", label: "Pratiche del Mese" },
  { to: "/campagne", label: "Campagne WA" },
  { to: "/inbox", label: "Inbox WhatsApp" },
  { to: "/coach", label: "Coach AI" },
  { to: "/comandante", label: "Comandante AI" },
  { to: "/aree-di-lavoro", label: "Aree di Lavoro" },
  { to: "/documenti", label: "Documenti Utili" },
  { to: "/crosssell", label: "Cross-sell AI" },
  { to: "/glossario", label: "Glossario Finanziarie/Aziende" },
  { to: "/statistiche", label: "Statistiche" },
  { to: "/confronto", label: "Confronto Mese" },
  { to: "/preventivatore", label: "Preventivatore PP" },
  { to: "/strumenti", label: "Calcolatori" },
  { to: "/messaggi", label: "Messaggi WA" },
  { to: "/campi", label: "Campi Custom" },
  { to: "/dati", label: "Import / Export" },
  { to: "/cronologia", label: "Recupero / Cronologia" },
  { to: "/team", label: "Team & Permessi" },
  { to: "/integrazioni/whatsapp", label: "WhatsApp / WAHA" },
  { to: "/audit", label: "Audit log" },
  { to: "/integrazioni", label: "Integrazioni" },
  { to: "/duplicati", label: "Pulizia Duplicati" },
];

export default function SidebarBuilderPage() {
  const [items, setItems] = useState([]);
  const [hidden, setHidden] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/prefs");
        const s = r.data?.preferences?.sidebar || {};
        const order = s.order || [];
        const h = s.hidden || [];
        setHidden(h);
        // Apply current order: items in `order` first, others append at end
        const idx = (to) => {
          const i = order.indexOf(to);
          return i === -1 ? 999 : i;
        };
        setItems([...ALL_ITEMS].sort((a, b) => idx(a.to) - idx(b.to)));
      } catch (e) {
        toast.error("Errore caricamento preferenze");
      } finally { setLoading(false); }
    })();
  }, []);

  const moveUp = (i) => {
    if (i === 0) return;
    const next = [...items];
    [next[i-1], next[i]] = [next[i], next[i-1]];
    setItems(next);
  };
  const moveDown = (i) => {
    if (i === items.length - 1) return;
    const next = [...items];
    [next[i+1], next[i]] = [next[i], next[i+1]];
    setItems(next);
  };
  const toggleHide = (to) => {
    setHidden((cur) => cur.includes(to) ? cur.filter((x) => x !== to) : [...cur, to]);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/prefs", { key: "sidebar", value: { order: items.map((i) => i.to), hidden } });
      toast.success("Sidebar personalizzata salvata. Aggiorna la pagina per vederla.");
    } catch { toast.error("Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Ripristinare la sidebar di default?")) return;
    setSaving(true);
    try {
      await api.patch("/prefs", { key: "sidebar", value: { order: [], hidden: [] } });
      setItems(ALL_ITEMS);
      setHidden([]);
      toast.success("Sidebar ripristinata");
    } catch { toast.error("Errore"); }
    finally { setSaving(false); }
  };

  if (loading) return <AppLayout><div className="flex justify-center py-20 w-6 h-6 animate-spin text-[#B7955B]"><Loader2 /></div></AppLayout>;

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="mb-6">
          <div className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-1" style={{ color: "var(--gold)" }}>Customization Layer</div>
          <h1 className="font-serif text-4xl text-slate-900">Personalizza Sidebar</h1>
          <p className="text-sm text-slate-500 mt-2">Riordina o nascondi le voci del menu laterale. Le preferenze sono salvate per il tuo account.</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-[3px] overflow-hidden">
          {items.map((it, i) => {
            const isHidden = hidden.includes(it.to);
            return (
              <div key={it.to} data-testid={`sb-item-${it.to}`}
                className={`flex items-center gap-2 px-4 py-2.5 border-b border-[#F0F2F5] last:border-b-0 ${isHidden ? "bg-slate-50/50 opacity-50" : ""}`}>
                <div className="flex flex-col gap-0.5">
                  <button onClick={() => moveUp(i)} disabled={i === 0} className="disabled:opacity-30 hover:text-[#B7955B]"><ArrowUp className="w-3 h-3" /></button>
                  <button onClick={() => moveDown(i)} disabled={i === items.length - 1} className="disabled:opacity-30 hover:text-[#B7955B]"><ArrowDown className="w-3 h-3" /></button>
                </div>
                <span className="text-slate-400 text-xs tabular-nums w-6">{i+1}</span>
                <span className={`flex-1 text-sm ${isHidden ? "line-through text-slate-400" : "text-slate-800"}`}>{it.label}</span>
                <code className="text-[10px] text-slate-400">{it.to}</code>
                <button onClick={() => toggleHide(it.to)} data-testid={`sb-toggle-${it.to}`}
                  className={`px-2 py-1 text-xs rounded-[3px] flex items-center gap-1 ${isHidden ? "bg-slate-100 text-slate-500 hover:bg-slate-200" : "bg-[#B7955B]/10 text-[#B7955B] hover:bg-[#B7955B]/20"}`}>
                  {isHidden ? <><EyeOff className="w-3 h-3"/>Nascosta</> : <><Eye />Visibile</>}
                </button>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between mt-6">
          <button onClick={reset} disabled={saving}
            className="px-3 py-2 text-sm border border-slate-300 rounded-[3px] hover:bg-white flex items-center gap-1.5 disabled:opacity-60">
            <RotateCcw className="w-3.5 h-3.5"/> Ripristina default
          </button>
          <button onClick={save} disabled={saving} data-testid="sb-save"
            className="px-4 py-2 text-sm bg-[#0A192F] text-white rounded-[3px] hover:bg-[#172A45] flex items-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Save />}
            Salva preferenze
          </button>
        </div>
      </div>
    </AppLayout>
  );
}
